import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  LayoutDashboard, Calendar, Upload, UtensilsCrossed, BedDouble, BarChart3, TrendingUp, FileEdit,
  Building2, Users, ScrollText, Plus, Search, Moon, Sun, Printer,
} from "lucide-react";

/**
 * AcenteCommandPalette — Ctrl+K / Cmd+K quick launcher.
 * Provides deep-links to every panel page + reservation search + theme toggle.
 * Role-aware: only actions the current user can perform are shown.
 */
export default function AcenteCommandPalette({ open, onOpenChange, role, onToggleTheme, isDark }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rez, setRez] = useState([]);

  // Live-search reservations while the query grows.
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setRez([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      panelApi
        .get("/reservations", { params: { search: q } })
        .then((r) => { if (!cancelled) setRez((r.data || []).slice(0, 8)); })
        .catch(() => { if (!cancelled) setRez([]); });
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  const go = (to) => { onOpenChange(false); nav(to); };
  const staff = ["admin", "operasyon"].includes(role);
  const isOps = role === "operasyon";
  const isOzel = role === "ozel_acente";
  const canBook = staff || role === "acente" || isOzel;
  // Bulk / group tools — operasyon focuses on pricing, so hide these for them.
  const canBulk = role === "admin" || role === "acente";
  const showRevisions = !isOzel;
  const showInvoices = !isOps; // operasyon doesn't see fatura talepleri anymore
  const showGroupTools = role === "admin" || role === "acente";

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <VisuallyHidden>
        <DialogTitle>Komut paleti — hızlı ara</DialogTitle>
      </VisuallyHidden>
      <CommandInput
        placeholder="Ara: rezervasyon no, grup adı, sayfa…"
        value={q}
        onValueChange={setQ}
        data-testid="cmd-palette-input"
      />
      <CommandList>
        <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>

        {rez.length > 0 && (
          <>
            <CommandGroup heading="Rezervasyonlar">
              {rez.map((r) => (
                <CommandItem
                  key={r.id}
                  value={`rez ${r.reservation_number} ${r.group_name}`}
                  onSelect={() => go(`/acente/rezervasyon/${r.id}`)}
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  <span className="font-mono text-xs mr-2 text-brand-orange">{r.reservation_number}</span>
                  <span className="truncate">{r.group_name}</span>
                  <span className="ml-auto text-xs text-slate-400">{formatTr(r.checkin)} → {formatTr(r.checkout)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Hızlı İşlemler">
          {canBook && (
            <CommandItem onSelect={() => go("/acente/rezervasyon/yeni")}>
              <Plus className="mr-2 h-4 w-4" /> Yeni Rezervasyon
              <CommandShortcut>N</CommandShortcut>
            </CommandItem>
          )}
          {canBulk && (
            <CommandItem onSelect={() => go("/acente/toplu-yukle")}>
              <Upload className="mr-2 h-4 w-4" /> Excel'den Toplu Yükle
            </CommandItem>
          )}
          <CommandItem onSelect={() => { onOpenChange(false); onToggleTheme?.(); }}>
            {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
            {isDark ? "Aydınlık Mod" : "Karanlık Mod"}
            <CommandShortcut>D</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => { onOpenChange(false); window.print(); }}>
            <Printer className="mr-2 h-4 w-4" /> Sayfayı Yazdır
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Sayfalar">
          <CommandItem onSelect={() => go("/acente")}><LayoutDashboard className="mr-2 h-4 w-4" /> Panel</CommandItem>
          <CommandItem onSelect={() => go("/acente/rezervasyonlar")}><Calendar className="mr-2 h-4 w-4" /> Rezervasyonlar</CommandItem>
          {["admin", "operasyon"].includes(role) && <CommandItem onSelect={() => go("/acente/bugun-girisler")}><Calendar className="mr-2 h-4 w-4" /> Bugün Girişler</CommandItem>}
          {["admin", "operasyon"].includes(role) && <CommandItem onSelect={() => go("/acente/takip")}><Calendar className="mr-2 h-4 w-4" /> Rezervasyon Takip</CommandItem>}
          {canBulk && <CommandItem onSelect={() => go("/acente/toplu-yukle")}><Upload className="mr-2 h-4 w-4" /> Toplu Yükle</CommandItem>}
          {!isOzel && <CommandItem onSelect={() => go("/acente/planlama-raporlar")}><BarChart3 className="mr-2 h-4 w-4" /> Planlama & Rapor</CommandItem>}
          {showRevisions && <CommandItem onSelect={() => go("/acente/revizyon-talepleri")}><FileEdit className="mr-2 h-4 w-4" /> Revizyon Talepleri</CommandItem>}
          {showInvoices && <CommandItem onSelect={() => go("/acente/fatura-talepleri")}><FileEdit className="mr-2 h-4 w-4" /> Fatura Talepleri</CommandItem>}
          {["admin", "operasyon"].includes(role) && <CommandItem onSelect={() => go("/acente/sifre-talepleri")}><FileEdit className="mr-2 h-4 w-4" /> Şifre Talepleri</CommandItem>}
          <CommandItem onSelect={() => go("/acente/ayarlar")}><FileEdit className="mr-2 h-4 w-4" /> Ayarlar</CommandItem>
          {role === "admin" && <CommandItem onSelect={() => go("/acente/acenteler")}><Building2 className="mr-2 h-4 w-4" /> Acenteler</CommandItem>}
          {role === "admin" && <CommandItem onSelect={() => go("/acente/kullanicilar")}><Users className="mr-2 h-4 w-4" /> Kullanıcılar</CommandItem>}
          {role === "admin" && <CommandItem onSelect={() => go("/acente/loglar")}><ScrollText className="mr-2 h-4 w-4" /> Sistem Logları</CommandItem>}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
